import { Flex } from "antd";
import { useCallback, useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import {
    getMarketGetByExternalId,
    getRouteFindAllByMarketId,
} from "tm-shared/api";
import { validationMessage } from "tm-shared/constants";
import { Option, UserRole } from "tm-shared/types";

import { RHFAutocomplete } from "@/components/RHF/RHFAutocomplete";
import { RHFTextField } from "@/components/RHF/RHFTextField";
import { PrimaryText } from "@/components/Typography/Styled";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import {
    FieldName,
    SUCCESS_ERR_CODE_CHECK,
} from "@/pages/SV/MarketCreate/MarketCreate.constants";
import { setMarketCreatePageModal } from "@/store/slices/ui";
import { NewMarketFormValues } from "@/types/form";
import { MarketCreateModal } from "@/types/modals";

interface PartnerSectionProps {
    activeMap: Record<FieldName, boolean>;
    resetBelow: (field: FieldName) => void;
    setIsCheckingPartnerExternalId: (value: boolean) => void;
    isCheckingPartnerExternalId: boolean;
    partners: Option[];
}
const DEFAULT_PARTNER_ID = "alcoline";

export const PartnerSection = ({
    activeMap,
    resetBelow,
    setIsCheckingPartnerExternalId,
    isCheckingPartnerExternalId,
    partners,
}: PartnerSectionProps) => {
    const { team } = useAppSelector((state) => state.sv);
    const { role } = useAppSelector((state) => state.user);

    const partnerId = team.partnerId ?? DEFAULT_PARTNER_ID;

    const partnerLabel =
        partners.find((p) => p.value === partnerId)?.label ?? partnerId;

    const dispatch = useAppDispatch();

    const { setError, getValues, setValue } =
        useFormContext<NewMarketFormValues>();

    const debounceTimeoutReference = useRef<ReturnType<
        typeof setTimeout
    > | null>(null);

    const validateExternalId = useCallback(
        async (externalId?: string) => {
            if (!externalId || externalId.length < 3) return;

            setIsCheckingPartnerExternalId(true);
            try {
                const marketResponse = await getMarketGetByExternalId({
                    externalId,
                });

                const routesResponse = await getRouteFindAllByMarketId({
                    marketId: marketResponse.result.id,
                });

                dispatch(
                    setMarketCreatePageModal({
                        type: MarketCreateModal.DuplicateById,
                        payload: {
                            market: marketResponse.result,
                            routeCount: routesResponse.result.length,
                        },
                    }),
                );

                setError("partnerExternalMarketId", {
                    message: "Маркет із таким ID вже існує",
                });
            } catch (error) {
                if (error && typeof error === "object" && "code" in error) {
                    if (error.code !== SUCCESS_ERR_CODE_CHECK) {
                        setError("partnerExternalMarketId", {
                            message: "Помилка при перевірці маркету",
                        });
                    }
                }
            } finally {
                setIsCheckingPartnerExternalId(false);
            }
        },
        [dispatch, setError, setIsCheckingPartnerExternalId],
    );

    const debouncedValidateExternalId = useCallback(
        async (externalId?: string) => {
            if (debounceTimeoutReference.current) {
                clearTimeout(debounceTimeoutReference.current);
            }

            if (!externalId || externalId.length < 3) return;

            await new Promise<void>((resolve) => {
                debounceTimeoutReference.current = setTimeout(() => {
                    void validateExternalId(externalId).finally(resolve);
                }, 1500);
            });
        },
        [validateExternalId],
    );

    useEffect(() => {
        return () => {
            if (debounceTimeoutReference.current) {
                clearTimeout(debounceTimeoutReference.current);
            }
        };
    }, []);

    useEffect(() => {
        const v = getValues("partnerExternalMarketId");

        void validateExternalId(v);
    }, [getValues, validateExternalId]);

    useEffect(() => {
        setValue("partner", partnerId, {
            shouldValidate: true,
            shouldDirty: false,
        });
    }, [role, partnerId, setValue]);

    return (
        <>
            {role === UserRole.Sv ? (
                <Flex vertical gap={4}>
                    <PrimaryText>Партнер:</PrimaryText>
                    <PrimaryText fontWeight="700">{partnerLabel}</PrimaryText>
                </Flex>
            ) : (
                <RHFAutocomplete
                    name="partner"
                    options={partners}
                    label="Партнер"
                    disabled={!activeMap.partner}
                    onAfterChange={() => resetBelow("partner")}
                    rules={{
                        required: validationMessage.required,
                    }}
                    allowClear={false}
                    selectProps={{ size: "large" }}
                />
            )}
            <RHFTextField
                name="partnerExternalMarketId"
                label="ID торгової точки в обліковому записі партнера"
                disabled={!activeMap.partnerExternalMarketId}
                onAfterChange={() => resetBelow("partnerExternalMarketId")}
                onAfterChangeAsync={debouncedValidateExternalId}
                loading={isCheckingPartnerExternalId}
                size="large"
                rules={{
                    minLength: {
                        value: 3,
                        message: validationMessage.minLength(3),
                    },
                }}
            />
        </>
    );
};
